import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, StripeCardElementChangeEvent } from '@stripe/stripe-js'
import { useMutation } from '@tanstack/react-query'
import Button from 'components/core/button'
import { getEnvVars } from 'hooks/use-env-vars'
import { Company } from 'interfaces/company'
import { BankAccount, PaymentMethod } from 'interfaces/payment-method'
import { request } from 'lib/api/fetch-api'
import Modal from 'lib/components/modals/modal'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'lib/components/tabs'
import TextInput from 'lib/components/text-input/text-input'
import { LoaderCircleIcon, LockIcon } from 'lucide-react'
import { queryClient } from 'providers/query-client-provider'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import BankAccountForm from './bank-account-form'
import { StripeCardElement } from './stripe-card-element'
interface BillingPaymentsModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  currentPaymentMethod: PaymentMethod
  company: Company
}

export default function BillingPaymentsModal({
  open,
  setOpen,
  currentPaymentMethod,
  company,
}: BillingPaymentsModalProps) {
  const { stripePublishableKey } = getEnvVars()
  const stripePromise = loadStripe(stripePublishableKey)

  return (
    <Modal open={open} setOpen={setOpen} size="md">
      <Modal.Header>Update Payment Method</Modal.Header>
      <Elements stripe={stripePromise}>
        <StripeContent setOpen={setOpen} currentPaymentMethod={currentPaymentMethod} company={company} />
      </Elements>
    </Modal>
  )
}

interface StripeContentProps {
  setOpen: (open: boolean) => void
  currentPaymentMethod: PaymentMethod
  company: Company
}

function StripeContent({ setOpen, currentPaymentMethod, company }: StripeContentProps) {
  const [cardholderName, setCardholderName] = useState(currentPaymentMethod.name ?? '')
  const [tab, setTab] = useState<string>(currentPaymentMethod.object)
  const [stripeCardInfo, setStripeCardInfo] = useState<StripeCardElementChangeEvent>()
  const [bankInfo, setBankInfo] = useState(
    currentPaymentMethod.object === 'bank_account'
      ? {
          accessToken: null,
          plaidAccountId: currentPaymentMethod.id,
          plaidAccountMetadata: {
            name: currentPaymentMethod.bankName,
            mask: currentPaymentMethod.last4,
          },
        }
      : {
          accessToken: null,
          plaidAccountId: null,
          plaidAccountMetadata: {
            name: null,
            mask: null,
          },
        },
  )

  const stripe = useStripe()
  const elements = useElements()

  const updatePaymentInfoMutation = useMutation({
    mutationFn: async ({ cardInfo, bankInfo }: { cardInfo?: StripeCardElementChangeEvent; bankInfo?: BankAccount }) => {
      const body = { payment_method: { card: null, bank: null } }

      if (cardInfo) {
        body.payment_method.card = await getCardInfo()
      }

      if (bankInfo) {
        body.payment_method.bank = getBankInfo()
      }

      return request({
        endpoint: 'updatePaymentMethod',
        query: { id: company.id },
        body,
      })
    },
    onSuccess: () => {
      setOpen(false)
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      toast.success('Payment method updated')
    },
    onError: (error) => {
      console.error('Failed to update payment method', error)
      toast.error('Failed to update payment method')
    },
  })

  const isUpdateButtonDisabled = useMemo(() => {
    if (tab === 'card') {
      return !stripeCardInfo?.complete || !!stripeCardInfo?.error || updatePaymentInfoMutation.isPending
    }
    return (
      !bankInfo.plaidAccountId ||
      !bankInfo.plaidAccountMetadata ||
      !bankInfo.accessToken ||
      updatePaymentInfoMutation.isPending
    )
  }, [tab, stripeCardInfo, bankInfo, updatePaymentInfoMutation.isPending])

  const getCardInfo = async () => {
    const cardElement = elements.getElement(CardElement)
    const tokenResponse = await stripe.createToken(cardElement, {
      name: cardholderName,
    })
    return { access_token: tokenResponse.token.id }
  }

  const getBankInfo = () => {
    return { account_id: bankInfo?.plaidAccountId, access_token: bankInfo?.accessToken }
  }

  const handleUpdatePaymentInfo = () => {
    updatePaymentInfoMutation.mutate({
      cardInfo: tab === 'card' ? stripeCardInfo : undefined,
      bankInfo: tab === 'bank_account' ? bankInfo : undefined,
    })
  }

  return (
    <>
      <Modal.Body>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList intent="background">
            <TabsTrigger value="card" intent="background">
              Credit Card
            </TabsTrigger>
            <TabsTrigger value="bank_account" intent="background">
              Bank Account
            </TabsTrigger>
          </TabsList>
          <TabsContent value="card" className="tw-mb-2">
            <>
              <TextInput
                label="Name on Card"
                placeholder="First Middle Last"
                type="text"
                value={cardholderName}
                name="cardholderName"
                onChange={(e) => setCardholderName(e.target.value)}
              />
              <StripeCardElement
                onChange={(e) => {
                  setStripeCardInfo(e)
                }}
              />
            </>
          </TabsContent>
          <TabsContent value="bank_account" className="tw-mb-4">
            <BankAccountForm bankInfo={bankInfo} setBankInfo={setBankInfo} />
          </TabsContent>
        </Tabs>
        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-picklegreen-500">
          <LockIcon className="tw-h-4 tw-w-4" /> <span>256-bit SSL Secured — Powered by Stripe and Plaid.</span>
        </span>
      </Modal.Body>
      <Modal.Footer>
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <Button color="lightGray" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button color="purple" onClick={handleUpdatePaymentInfo} disabled={isUpdateButtonDisabled}>
            {updatePaymentInfoMutation.isPending ? <LoaderCircleIcon className="tw-animate-spin" /> : 'Update'}
          </Button>
        </div>
      </Modal.Footer>
    </>
  )
}
